import CONFIG from "config/config";
import { authorization } from "../auth";
import { INTEGRATIONS, Integration } from "../integrations/dto";

/**
 * API client to communicate with OPPIZI API
 */
class APIClient {
  /**
   * API Path
   * @type Object
   */
  path = {
    payment: "/payment",
    locations: "/locations",
    locationTypes: "/location-types",
    couponCode: "/get-coupons",
    promotionCode: "/get-promotion_code_id",
    customer: "/get-customer",
    embeddedCheckout: "/embedded-checkout",
    checkoutSession: "/session",
  };

  /**
   * Get base API URL without /platform suffix
   */
  getApiRootUrl = () => {
    return CONFIG.API_URL;
  };

  getEnableUSSalesTax = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const enableUSSalesTax = urlParams.get("enableUSSalesTax");
    return enableUSSalesTax === "true";
  };

  /**
   * Prepare request
   * @returns {Request}
   */
  prepareRequest(url, method = "GET", body = null) {
    const headers = new Headers({
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-api-key": authorization.getAPIKey(),
    });

    const initParam = { method, headers };

    if (body) {
      initParam["body"] = JSON.stringify(body);
    }

    /* eslint-disable */
    console.log(`Request: [${method}] ${url}`);
    /* eslint-enable */

    return new Request(url, initParam);
  }

  checkoutCampaign = ({
    campaignId,
    clientId,
    campaignName,
    grand,
    currencyCode,
    countryCode,
    promotionId,
    customerId,
    userId,
    isDM,
  }) => {
    const request = this.prepareRequest(`${CONFIG.PAYMENT_BASE_URL}${this.path.payment}`, "POST", {
      campaignId,
      clientId,
      campaignName,
      grand,
      currencyCode,
      countryCode,
      promotionId,
      customerId,
      userId,
      isDM,
    });
    return fetch(request);
  };

  embeddedCheckoutCampaign = ({
    campaignId,
    clientId,
    campaignName,
    grand,
    currencyCode,
    countryCode,
    promotionId,
    customerId,
    userId,
    subtype,
    campaignDetailedCosts,
    discountChoice,
  }) => {
    const enableUSSalesTax = this.getEnableUSSalesTax();
    const request = this.prepareRequest(`${CONFIG.PAYMENT_BASE_URL}${this.path.embeddedCheckout}`, "POST", {
      campaignId,
      clientId,
      campaignName,
      grand,
      currencyCode,
      countryCode,
      promotionId,
      customerId,
      userId,
      subtype,
      campaignDetailedCosts,
      enableUSSalesTax,
      // HUB-458: docs/review-discount-payments-integration.md §2.0. The
      // caller passes `undefined` when is_review_discount_enabled is off, so
      // JSON.stringify drops the key entirely (zero-footprint) — never send
      // an explicit `null` unless the flag is genuinely on with nothing
      // chosen.
      discount_choice: discountChoice,
    });

    return fetch(request);
  };

  embeddedCheckoutCampaignSession = (sessionId, countryCode) => {
    const request = this.prepareRequest(
      `${CONFIG.PAYMENT_BASE_URL}${this.path.embeddedCheckout}${this.path.checkoutSession}/${sessionId}?countryCode=${countryCode}`,
      "GET"
    );
    return fetch(request);
  };

  getCouponCode = ({ couponCode, countryCode }) => {
    const request = this.prepareRequest(
      `${CONFIG.PAYMENT_BASE_URL}${this.path.couponCode}/?id=${couponCode}&countryCode=${countryCode}`,
      "GET"
    );
    return fetch(request);
  };

  getPromotionCode = ({ promotionCode, countryCode }) => {
    const request = this.prepareRequest(
      `${CONFIG.PAYMENT_BASE_URL}${this.path.promotionCode}/?promotionCode=${promotionCode}&countryCode=${countryCode}`,
      "GET"
    );
    return fetch(request);
  };

  getCustomer = ({ id, countryCode }) => {
    const request = this.prepareRequest(
      `${CONFIG.PAYMENT_BASE_URL}${this.path.customer}/?id=${id}&countryCode=${countryCode}`,
      "GET"
    );
    return fetch(request);
  };

  getLocationTypes = () => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}${this.path.locationTypes}`, "GET");
    return fetch(request);
  };

  createLocation = (body) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}${this.path.locations}`, "POST", body);
    return fetch(request);
  };

  deleteLocation = (body) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}${this.path.locations}`, "DELETE", body);
    return fetch(request);
  };

  updateLocation = (body, locationId) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}${this.path.locations}/${locationId}`, "PUT", body);
    return fetch(request);
  };

  getPresignedUrl = (body) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/audience/document-presigned-url`, "POST", body);
    return fetch(request);
  };

  getAudienceDownloadUrl = (body) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/audience/document-download-url`, "POST", body);
    return fetch(request);
  };

  createAudience = (body) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/audience`, "POST", body);
    return fetch(request);
  };

  deleteAudience = (audienceId) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/audience/${audienceId}`, "DELETE");
    return fetch(request);
  };

  updateAudience = ({ audienceId, body }) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/audience/${audienceId}`, "PUT", body);
    return fetch(request);
  };

  getClientIntegrations = (clientId: number) => {
    const request = this.prepareRequest(`${this.getApiRootUrl()}/integrations/${clientId}`, "GET");
    return fetch(request);
  };

  getIntegrations = (clientId: number, provider: INTEGRATIONS, options?: { onlyConnected?: boolean }) => {
    const request = this.prepareRequest(`${this.getApiRootUrl()}/integrations/${provider}/${clientId}`, "GET");
    return fetch(request);
  };

  getClientAudiences = async (clientId, offset = 0, limit = 50) => {
    try {
      const url = new URL(`${CONFIG.API_BASE_URL}/audience`);
      url.searchParams.append("client_id", clientId);
      url.searchParams.append("limit", limit);
      url.searchParams.append("offset", offset);

      const request = this.prepareRequest(url, "GET");
      const response = await fetch(request);
      if (response.status !== 200) {
        throw new Error("Failed to fetch client audiences");
      }
      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error("Unexpected response format");
      }

      return data;
    } catch (error) {
      console.error("Error fetching client audiences:", error);
      return [];
    }
  };

  getAudiencesByIds = async (audienceIds) => {
    try {
      if (!audienceIds || audienceIds.length === 0) {
        return [];
      }
      const idsParam = Array.isArray(audienceIds) ? audienceIds.join(",") : String(audienceIds);
      const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/audience/${idsParam}`, "GET");
      const response = await fetch(request);

      if (response.status !== 200) {
        throw new Error("Failed to fetch audiences by ids");
      }

      const data = await response.json();
      return Array.isArray(data) ? data : [data];
    } catch (error) {
      console.error("Error fetching audiences by ids:", error);
      return [];
    }
  };

  getAllClientAudiences = async (clientId) => {
    try {
      const limit = 50;

      const fetchAllAudiences = async (offset, accumulatedAudiences = []) => {
        const url = new URL(`${CONFIG.API_BASE_URL}/audience`);
        url.searchParams.append("client_id", clientId);
        url.searchParams.append("limit", limit);
        url.searchParams.append("offset", offset);

        const request = this.prepareRequest(url, "GET");
        const response = await fetch(request);
        if (response.status !== 200) {
          throw new Error("Failed to fetch client audiences");
        }
        const data = await response.json();

        if (!Array.isArray(data)) {
          throw new Error("Unexpected response format");
        }

        const allAudiences = [...accumulatedAudiences, ...data];

        if (data.length === limit) {
          return fetchAllAudiences(offset + limit, allAudiences);
        }

        return allAudiences;
      };

      return await fetchAllAudiences(0);
    } catch (error) {
      console.error("Error fetching all client audiences:", error);
      return [];
    }
  };

  createCampaignAudience = ({ audience_id, campaign_id }) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}/audience/${audience_id}/campaigns/${campaign_id}`,
      "POST"
    );
    return fetch(request);
  };

  deleteCampaignAudience = ({ audience_id, campaign_id }) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}/audience/${audience_id}/campaigns/${campaign_id}`,
      "DELETE"
    );
    return fetch(request);
  };

  getClientVariables = async (clientId, offset = 0, limit = 50, options = {}) => {
    try {
      const url = new URL(`${CONFIG.API_BASE_URL}/variable`);
      url.searchParams.append("client_id", clientId);
      url.searchParams.append("limit", limit);
      url.searchParams.append("offset", offset);

      if (options.unassigned) {
        url.searchParams.append("unassigned", "true");
      }

      const request = this.prepareRequest(url, "GET");
      const response = await fetch(request);
      if (response.status !== 200) {
        throw new Error("Failed to fetch client variables");
      }
      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error("Unexpected response format");
      }

      return data;
    } catch (error) {
      console.error("Error fetching client variables:", error);
      return [];
    }
  };

  linkVariableWithCampaign = (campaignId, variableId) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}/variable/campaign/${campaignId}/variable/${variableId}`,
      "POST"
    );
    return fetch(request);
  };

  unlinkVariableWithCampaign = (campaignId, variableId) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}/variable/campaign/${campaignId}/variable/${variableId}`,
      "DELETE"
    );
    return fetch(request);
  };

  getCampaignVariables = (campaignId) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/variable/campaign-variables/${campaignId}`, "GET");
    return fetch(request);
  };

  getVariablePresignedUrl = (body) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/variable/document-presigned-url`, "POST", body);
    return fetch(request);
  };

  getVariableDownloadUrl = (body) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/variable/document-download-url`, "POST", body);
    return fetch(request);
  };

  createVariable = (body) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/variable`, "POST", body);
    return fetch(request);
  };

  updateVariable = (id, body) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/variable/${id}`, "PUT", body);
    return fetch(request);
  };

  getCampaignAudiences = (campaignId) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/audience/campaign-audiences/${campaignId}`, "GET");
    return fetch(request);
  };

  linkAudienceWithCampaign = (campaignId, audienceId) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}/audience/${audienceId}/campaigns/${campaignId}`,
      "POST"
    );
    return fetch(request);
  };

  unlinkAudienceWithCampaign = (campaignId, audienceId) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}/audience/${audienceId}/campaigns/${campaignId}`,
      "DELETE"
    );
    return fetch(request);
  };

  checkCreatedAudiencesZips = (body) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/audience/created-audience-zip`, "POST", body);
    return fetch(request);
  };

  deleteAudienceZip = (audienceId) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/audience/audience-zip/${audienceId}`, "DELETE");
    return fetch(request);
  };

  updateCampaignReviewMeetingDate = (campaignId, reviewMeetingDate) => {
    const request = this.prepareRequest(`${CONFIG.API_BASE_URL}/campaigns/${campaignId}/review-meeting`, "PUT", {
      reviewMeetingDate,
    });
    return fetch(request);
  };

  sendCampaignLinkToEmail = (url) => {
    const request = this.prepareRequest(
      `${CONFIG.API_BASE_URL}/campaigns/us/direct-mail/adm/send-desktop-link?url=${url}`,
      "POST",
      { url }
    );
    return fetch(request);
  };

  getLatestReturnAddress = () => {
    const request = this.prepareRequest(`${this.getApiRootUrl()}/direct-mail/return-addresses/latest`, "GET");
    return fetch(request);
  };

  saveReturnAddress = (body) => {
    const request = this.prepareRequest(`${this.getApiRootUrl()}/direct-mail/return-addresses`, "POST", body);
    return fetch(request);
  };

  getAllReturnAddresses = () => {
    const request = this.prepareRequest(`${this.getApiRootUrl()}/direct-mail/return-addresses/all`, "GET");
    return fetch(request);
  };

  deleteReturnAddress = (id) => {
    const request = this.prepareRequest(`${this.getApiRootUrl()}/direct-mail/return-addresses/${id}`, "DELETE");
    return fetch(request);
  };

  updateWorkflow = (uuid, body) => {
    const request = this.prepareRequest(`${this.getApiRootUrl()}/direct-mail/workflows/${uuid}`, "PUT", body);
    return fetch(request);
  };

  /**
   * Resolve a batch of user IDs to minimal display info (name + avatar).
   * Backed by `POST /platform/users/resolve`. Accepts Liveblocks ids
   * ("user-123") or raw numeric ids. Returns an array of
   * `{ id, name, avatar }` objects where `id` is always Liveblocks-formatted.
   */
  resolveUsersBatch = async ({ ids } = {}) => {
    try {
      if (!Array.isArray(ids) || ids.length === 0) return [];
      const url = `${CONFIG.API_BASE_URL}/users/resolve`;
      const request = this.prepareRequest(url, "POST", { ids });
      const response = await fetch(request);
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data?.users) ? data.users : [];
    } catch (e) {
      return [];
    }
  };

  /**
   * Fire-and-forget: email notification to users @-mentioned in a comment.
   * Backed by `POST /platform/comments/notify-mention`. The backend handles
   * tenant scoping and silently drops anyone the caller isn't allowed to notify.
   */
  notifyCommentMention = async ({ mentionedUserIds, designId, designName, designUrl, bodyPreview, threadId } = {}) => {
    try {
      if (!Array.isArray(mentionedUserIds) || mentionedUserIds.length === 0) return { sent: 0 };
      const url = `${CONFIG.API_BASE_URL}/comments/notify-mention`;
      const request = this.prepareRequest(url, "POST", {
        mentionedUserIds,
        designId,
        designName,
        designUrl,
        bodyPreview,
        threadId,
      });
      const response = await fetch(request);
      if (!response.ok) return { sent: 0 };
      return await response.json();
    } catch (e) {
      return { sent: 0 };
    }
  };

  /**
   * Search users in the current company for @-mention autocomplete.
   * Backed by `GET /platform/users/mention-search` — minimal shape, scoped
   * to the JWT's client, capped at 20 results, active users only.
   */
  searchMentionUsers = async ({ q } = {}) => {
    try {
      const url = new URL(`${CONFIG.API_BASE_URL}/users/mention-search`);
      if (q) url.searchParams.append("q", String(q));
      const request = this.prepareRequest(url, "GET");
      const response = await fetch(request);
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data?.users) ? data.users : [];
    } catch (e) {
      return [];
    }
  };

  /**
   * Cosmetically shorten a single URL via OPPIZI API.
   * `POST /create-short-url` returns `[{ shortUrl: <hash>, hash, url, id }]`.
   * Returns the full short link `${API_URL}/short/<hash>`, or null on failure
   * (caller falls back to the raw link so the QR never breaks).
   */
  createShortUrl = async (url) => {
    try {
      if (!url) return null;
      const request = this.prepareRequest(`${CONFIG.API_URL}/create-short-url`, "POST", {
        linksData: { qr: url },
      });
      const response = await fetch(request);
      if (!response.ok) return null;
      const data = await response.json();
      const hash = Array.isArray(data) ? data[0]?.hash : null;
      return hash ? `${CONFIG.API_URL}/short/${hash}` : null;
    } catch (e) {
      return null;
    }
  };
}
export const apiClient = new APIClient();
